<template>
  <AIBasePanel :title="getLocaleText('ai_summary_cluster_panel.title')">
    <template #toolbarLeft>
      <BaseButton
        circle
        data-testid="back-button"
        flat
        icon="chevron-left"
        :label="getLocaleText('ai_summary_cluster_panel.back_button')"
        new-design
        new-icon
        @click="back"
      />
    </template>

    <div
      v-if="stream && aiSummaryTopic"
      class="tw:flex tw:h-full tw:flex-col tw:gap-4"
    >
      <AIAssistantBaseChatType
        html
        @complete="showMentions = true"
      >
        <i18n-t
          keypath="ai_summary_cluster_panel.description"
          scope="global"
        >
          <template #clusterTitle>
            <span class="tw:font-bold">
              {{ aiSummaryTopic.title }}
            </span>
          </template>
          <template #streamLabel>
            <span class="tw:font-bold">{{ stream.label }}</span>
          </template>
        </i18n-t>
      </AIAssistantBaseChatType>
      <MentionStream
        v-if="showMentions"
        :fetching-options="fetchingOptions"
        :fetching-service="fetchMentions"
        hide-button-bar
        hide-notification-watcher
        hide-range-selector
        :stream="stream"
      />
    </div>
  </AIBasePanel>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, onMounted, ref } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import AIAssistantBaseChatType from "shared/components/AI/AIAssistant/AIAssistantBaseChatType.vue";
import AIBasePanel from "shared/components/AI/AIBasePanel.vue";
import BaseButton from "shared/components/base/BaseButton.vue";
import MentionStream from "shared/components/MentionStream.vue";
import { type MentionsOptions } from "shared/composables/useRequestMentions";
import fetchMentions from "shared/services/fetching/fetchMentions";
import useAIStore, { type AISummaryResultTopic } from "shared/stores/ai";
import useStreamsStore from "shared/stores/streams";
import { type Nullable, type Stream } from "shared/types";

const aiStore = useAIStore();

const {
  aiSummarySelectedTopic,
  aiStreamSummary,
  aiSummaryStreamId,
  aiSummaryChatHistory,
} = storeToRefs(aiStore);

const { openAIModal } = aiStore;

const streamsStore = useStreamsStore();
const { getStreamById } = streamsStore;

const stream = ref<Nullable<Stream>>(null);
const showMentions = ref<boolean>(false);

const aiSummaryTopic = computed<AISummaryResultTopic | undefined>(() => {
  if (aiSummaryChatHistory.value) {
    return aiSummaryChatHistory.value.result?.topics.find(
      (topic) => topic.id === aiSummarySelectedTopic.value?.topicId
    );
  }

  return aiStreamSummary.value.result?.topics.find(
    (topic) => topic.id === aiSummarySelectedTopic.value?.topicId
  );
});

const fetchingOptions = computed<MentionsOptions>(() => {
  if (
    aiSummaryChatHistory.value &&
    aiSummaryChatHistory.value.result &&
    aiSummaryTopic.value
  ) {
    return {
      endpoint: `ai/summarisations/${aiSummaryChatHistory.value.result.id}/summarisation_topics/${aiSummaryTopic.value.id}/mentions`,
      range: aiSummaryChatHistory.value.dateRange,
    };
  }

  if (!aiStreamSummary.value.result || !aiSummaryTopic.value) return {};

  return {
    endpoint: `ai/summarisations/${aiStreamSummary.value.result.id}/summarisation_topics/${aiSummaryTopic.value.id}/mentions`,
    range: aiStreamSummary.value.dateRange,
  };
});

function back() {
  if (aiSummaryChatHistory.value) {
    openAIModal("AIChatHistoryPanel");

    return;
  }

  if (aiSummarySelectedTopic.value?.assistant) {
    openAIModal("AISummaryAssistantPanel");

    return;
  }

  openAIModal("AISummaryPanel");
}

onMounted(() => {
  if (!aiSummaryStreamId.value) {
    return;
  }

  const streamFromStore = getStreamById(aiSummaryStreamId.value);

  if (!streamFromStore) {
    return;
  }

  stream.value = streamFromStore;
});
</script>
